/**
 * UX-09 — map stream.error payloads to clinician-facing copy with a clear
 * next action. The backend now sends a stable `error_type` plus safe copy;
 * this mapper prefers the code (so the action is always right even if backend
 * copy drifts) and falls back to the payload message, then to a generic line.
 * It must never render raw internals — the backend already guarantees the
 * message is safe, but unknown codes keep the message only if it reads like
 * sentence copy (no traceback markers).
 */

const CODE_COPY: Record<string, string> = {
  permission_denied:
    "You don't have permission to run this request for this patient. No data was changed. " +
    'If you believe you should have access, contact your administrator.',
  patient_data_unavailable:
    "This patient's chart data isn't available in the current session. " +
    'Relaunch the patient from the EHR to refresh the session, then try again.',
  upstream_llm_error:
    'The clinical AI service is temporarily unavailable or overloaded. ' +
    'Your patient data is safe. Please try again in a moment.',
  database_unavailable:
    'A clinical data service is temporarily unreachable. Your patient data is safe. ' +
    'Please retry shortly; if this keeps happening, contact your administrator.',
  guardrail_blocked:
    "This request couldn't be processed. It may be outside what I can help with — " +
    "try rephrasing, or ask about your patient's care.",
  mfa_required:
    'Multi-factor authentication is required. Please complete verification and try again.',
  session_expired:
    'Your session has expired. Relaunch the patient from the EHR to start a new session.',
  tool_timeout:
    'The request took longer than expected and was stopped. ' +
    'Try again, or narrow the question into smaller steps.',
  cancelled: 'Request cancelled.',
  internal_error:
    'Something went wrong while processing this request, and no answer was produced. ' +
    'Please try again; if it keeps failing, contact your administrator with the time this happened.',
};

/** Traceback-ish content that must never be shown to a clinician. */
const INTERNAL_MARKERS = [
  'traceback',
  'exception',
  'stack',
  '.py',
  'asyncpg',
  'duckdb.',
  'sqlstate',
  'errno',
];

function looksInternal(message: string): boolean {
  const low = message.toLowerCase();
  return INTERNAL_MARKERS.some((m) => low.includes(m));
}

export function streamErrorDisplayText(data: unknown): string {
  const d = (data ?? {}) as { error_type?: unknown; message?: unknown };
  const code = String(d.error_type ?? '').trim().toLowerCase();
  if (code && CODE_COPY[code]) return CODE_COPY[code];

  const message = String(d.message ?? '').trim();
  if (message && !looksInternal(message)) return message;
  return CODE_COPY.internal_error;
}
